import { action } from "mobx";
import { observer } from "mobx-react-lite";

import { Stack } from "@bps/fluent-ui";
import { routes } from "@libs/routing/routes.ts";
import { unlinkEpisodeOfCareFromEncounter } from "@stores/acc/utils/acc.utils.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { ButtonsGroupSingleChoiceField } from "@ui-components/form/ButtonsGroupSingleChoiceField.tsx";
import { SubmissionFormDialog } from "@ui-components/form/submission-form-dialog/SubmissionFormDialog.tsx";

import {
  ReviewConditionModalValues,
  reviewConditionNameOf,
  ReviewConditionUnlinkKeys
} from "./ReviewConditionModal.types.ts";
import { ReviewConditionModalField } from "./ReviewConditionModalField.tsx";
import { ReviewConditionModalWarning } from "./ReviewConditionModalWarning.tsx";

export const ReviewConditionModal = observer(() => {
  const root = useStores();
  const { booking, clinical, routing } = useStores();
  const { reviewConditionOptions } = booking.ui;

  if (!reviewConditionOptions) return null;

  const {
    calendarEvent,
    calendarEventEpisodeOfCare,
    encounterClaimEpisodeOfCare,
    encounterEpisodeOfCare,
    encounter
  } = reviewConditionOptions;

  const onDismiss = action(() => {
    booking.ui.reviewConditionOptions = undefined;
  });

  const onSubmit = async (values: ReviewConditionModalValues) => {
    const calendarEventEpisodeOfCareId = calendarEvent.episodeOfCareId;
    if (values.apptCondition && encounter && calendarEventEpisodeOfCareId) {
      // if the user has selected to update the consult condition to match the appointment
      const clinicalRecord = await clinical.getRecord(encounter.patientId, {
        encounterId: encounter.id
      });
      await unlinkEpisodeOfCareFromEncounter(clinicalRecord, root);
      await clinical.updateEncounter({
        id: encounter.id,
        eTag: encounter.eTag,
        episodeOfCareId: calendarEventEpisodeOfCareId
      });
    }

    if (values.consultCondition && encounterClaimEpisodeOfCare) {
      // if the user has selected to update the appointment condition to match the consult
      await booking.updateCalendarEvent({
        id: calendarEvent.id,
        episodeOfCareId: encounterClaimEpisodeOfCare.episodeOfCareId
      });
    }

    if (values.unlinkCondition === ReviewConditionUnlinkKeys.unlink) {
      // if the user has selected to remove the encounter condition to match the appointment
      const clinicalRecord = await clinical.getRecord(encounter.patientId, {
        encounterId: encounter.id
      });
      await unlinkEpisodeOfCareFromEncounter(clinicalRecord, root);
    }

    // then navigate to the consult
    if (calendarEvent.contactId) {
      routing.push(
        routes.records.appointment.path({
          id: calendarEvent.contactId,
          calendarEventId: calendarEvent.id
        }),
        routing.getStateWithFromQuery()
      );
    }
  };

  const text1 = calendarEventEpisodeOfCare
    ? "The consult condition is different to the condition linked to the appointment"
    : "The condition linked to the consult is no longer linked to the appointment";

  const text2 = calendarEventEpisodeOfCare
    ? "Please select the condition the consult will be linked to"
    : "Please select if the consult condition should be unlinked or have no change";

  return (
    <SubmissionFormDialog<ReviewConditionModalValues>
      dialogName="Review condition"
      dialogProps={{
        dialogContentProps: {
          title: "Review condition"
        },
        onDismiss,
        maxWidth: 480
      }}
      buttonsProps={{
        submitButtonProps: {
          label: "Save and continue"
        },
        promptOnCancel: false
      }}
      onSubmit={onSubmit}
    >
      {() => (
        <Stack tokens={{ childrenGap: 16 }}>
          <Stack>{text1}</Stack>
          <Stack>{text2}</Stack>

          <ReviewConditionModalField
            episodeOfCare={encounterEpisodeOfCare}
            name={reviewConditionNameOf("consultCondition")}
            heading="Consult condition"
            readOnly={!calendarEventEpisodeOfCare}
          />

          {calendarEventEpisodeOfCare && (
            <ReviewConditionModalField
              episodeOfCare={calendarEventEpisodeOfCare}
              name={reviewConditionNameOf("apptCondition")}
              heading="Appointment condition"
            />
          )}

          {!calendarEventEpisodeOfCare && (
            <ButtonsGroupSingleChoiceField
              required
              name={reviewConditionNameOf("unlinkCondition")}
              label="Update consult condition"
              options={[
                {
                  key: ReviewConditionUnlinkKeys.unlink,
                  text: "Unlink condition"
                },
                { key: ReviewConditionUnlinkKeys.noChange, text: "No change" }
              ]}
            />
          )}

          <ReviewConditionModalWarning />
        </Stack>
      )}
    </SubmissionFormDialog>
  );
});
